import { faRotate } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  TableRow,
  CustomCard,
  PageHeading,
  HeaderColumn,
  BootstrapTable,
  TableDetailCell,
  LastHeaderColumn,
  FirstHeaderColumn,
  TableDetailNameCell,
  WebsiteStatusBadge
} from "../common-styling";
import {
  fetchWebsiteCreationLogs,
  regenerateCustomerWebsite
} from "../../services/api-declaration";
import TableLoader from "../loader/table-loader";
import NoResultsRow from "../no-results-table-row";
import { showError } from "../../services/toast-service";
import CustomPagination from "../pagination/custom-pagination";
import { useTranslation } from "react-i18next";
import AdvancedFilters from "../table-filter/advanced-filter";
import { websiteLogsFilterConfig } from "../funnels-table/filter-config";
import { generateDateTime } from "../../services/common-service";
import {
  CUSTOMER_SITE_CREATION_ERROR_LOGS,
  CUSTOMER_SITE_CREATION_STATUS
} from "../strings";

const getStatusBadge = (status) => {
  const statusMap = {
    RECEIVED: { color: "#ffffff", background: "#808080" },
    PROCESSING: { color: "#fa8c16", background: "#fff7e6", border: "#ffd591" },
    COMPLETED: { color: "#52c41a", background: "#f6ffed", border: "#b7eb8f" },
    FAILED: { color: "#f5222d", background: "#fff1f0", border: "#ffa39e" }
  };

  return statusMap[status];
};

const WebsiteCreationLogs = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [logsData, setLogsData] = useState([]);
  const [paginateOpts, setPaginateOpts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const colSpan = 6;
  const [show, setShow] = useState(false);

  const toggleModal = () => setShow(!show);

  const handleQueryParams = (property, value) => {
    searchParams.set(property, value);
    setSearchParams(searchParams);
  };

  const generateErrorMessage = (err) => {
    switch (err) {
      case CUSTOMER_SITE_CREATION_ERROR_LOGS.AI_CONTENT_GENERATION_FAILED:
        return t("customerWebsite.errors.aiContentError");
      case CUSTOMER_SITE_CREATION_ERROR_LOGS.CUSTOMER_CREATION_FAILED:
        return t("customerWebsite.errors.customerCreationError");
      default:
        return "---";
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }
      const response = await fetchWebsiteCreationLogs(params);
      const respData = response.data.data;
      if (!respData.data.length) {
        setLogsData([]);
        setPaginateOpts({});
        return;
      }
      setLogsData(respData.data);
      setPaginateOpts({
        ...respData.metadata,
        rowsOnPage: respData.data.length
      });
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          t("toastMessage.errorInGetting", {
            what: t("sidebar.websiteLogs")
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataCB = useCallback(fetchData, [searchParams, t]);

  const retry = async (id) => {
    try {
      await regenerateCustomerWebsite(id);

      setLogsData((prevLogs) =>
        prevLogs.map((log) =>
          log._id === id
            ? { ...log, status: CUSTOMER_SITE_CREATION_STATUS.PROCESSING }
            : log
        )
      );
    } catch (error) {
      showError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    fetchDataCB();
  }, [fetchDataCB]);

  const WebsiteLogStatusBadge = ({ status }) => {
    return (
      <WebsiteStatusBadge $stage={getStatusBadge(status)}>
        <span>{t(`customerWebsite.${status}`)}</span>
      </WebsiteStatusBadge>
    );
  };

  return (
    <>
      <div>
        <PageHeading>{t("sidebar.websiteLogs")}</PageHeading>
        <div className="mt-3">
          <AdvancedFilters
            components={websiteLogsFilterConfig}
            pageName="search"
            handleModal={toggleModal}
            hideAddNew
            placeholder={t("searchByEmailPlaceholder")}
          />
        </div>
        <CustomCard className="mt-4 mb-3 p-3 rounded-0">
          <BootstrapTable responsive>
            <thead>
              <tr>
                <FirstHeaderColumn>
                  {t("common.customer")} {t("common.email")}
                </FirstHeaderColumn>
                <HeaderColumn className="text-center">Status</HeaderColumn>
                <HeaderColumn className="text-center">
                  {t("common.requestTime")}
                </HeaderColumn>
                <HeaderColumn className="text-center" colSpan={2}>
                  {t("common.errorMsg")}
                </HeaderColumn>
                <LastHeaderColumn className="text-end align-middle px-5">
                  {t("common.action")}
                </LastHeaderColumn>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableLoader colSpan={colSpan} />
              ) : logsData.length ? (
                logsData.map((item) => (
                  <TableRow key={item._id} className="optimization-table-row">
                    <TableDetailNameCell className="py-3">
                      {item?.customerEmail}
                    </TableDetailNameCell>
                    <TableDetailCell className="py-3 text-center">
                      <WebsiteLogStatusBadge status={item?.status} />
                    </TableDetailCell>
                    <TableDetailCell className="py-3 text-center">
                      {generateDateTime(item?.createdAt)}
                    </TableDetailCell>
                    <TableDetailCell className="py-3 text-center" colSpan={2}>
                      {generateErrorMessage(item?.errorMessage)}
                    </TableDetailCell>
                    <TableDetailCell className="text-end align-middle px-5">
                      {item?.status === CUSTOMER_SITE_CREATION_STATUS.FAILED ? (
                        <>
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip>{`Retry count: ${item?.retryCount}`}</Tooltip>
                            }
                          >
                            <FontAwesomeIcon
                              size="lg"
                              className="me-2 t-success cursor-pointer"
                              icon={faRotate}
                              onClick={() => {
                                retry(item._id);
                              }}
                            />
                          </OverlayTrigger>
                        </>
                      ) : (
                        <></>
                      )}
                    </TableDetailCell>
                  </TableRow>
                ))
              ) : (
                <NoResultsRow lengthOfColumns={colSpan} />
              )}
            </tbody>
          </BootstrapTable>
        </CustomCard>
        {logsData.length ? (
          <CustomPagination
            paginateOpts={paginateOpts}
            activeTab={Number(searchParams.get("pageIndex")) || 1}
            select={(val) => handleQueryParams("pageIndex", val)}
          />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
export default WebsiteCreationLogs;
